import { useEffect, useState, type ReactNode } from 'react'
import { getAdminApiClient } from '@/services/api'
import { useAuth } from '@/hooks/useAuth'
import type { UiConfig } from '@/types/api.types'
import {
  DEFAULT_FEATURE_FLAGS,
  FeatureFlagsContext,
} from './featureFlagsContext'

/**
 * Fetches the dashboard feature flags from the API once on mount and exposes
 * them via context. Mounted inside the authenticated app, so the API client
 * has a token available (the /config/ui endpoint itself is public). On error
 * the flags stay at their fail-closed defaults (all off).
 */
export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { getAccessToken } = useAuth()
  const [flags, setFlags] = useState<UiConfig>(DEFAULT_FEATURE_FLAGS)

  useEffect(() => {
    const controller = new AbortController()
    getAdminApiClient(getAccessToken)
      .getUiConfig(controller.signal)
      .then(setFlags)
      .catch((err) => {
        if (controller.signal.aborted) return
        console.error('Failed to load UI feature flags; defaulting to off', err)
      })
    return () => controller.abort()
  }, [getAccessToken])

  return (
    <FeatureFlagsContext.Provider value={flags}>
      {children}
    </FeatureFlagsContext.Provider>
  )
}
